import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, ReceiptText } from 'lucide-react'

import { ReportFilterBar } from '@/components/reports/ReportFilterBar'
import {
  exportVatSummaryReport,
  fetchVatSummaryReport,
  formatReportDate,
  formatReportMoney,
  resolveReportPeriodBounds,
} from '@/lib/api/reports'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { useBranch } from '@/contexts/BranchContext'
import { useReportPanelFilters } from '@/contexts/ReportPreferencesContext'

export function VatSummaryReportPanel() {
  const { activeBranchId } = useBranch()
  const { period, setPeriod, compare, setCompare, customFrom, setCustomFrom, customTo, setCustomTo } =
    useReportPanelFilters()
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const bounds = useMemo(() => {
    try {
      return resolveReportPeriodBounds(period, { from: customFrom, to: customTo })
    } catch {
      return null
    }
  }, [period, customFrom, customTo])

  const reportOptions = useMemo(
    () => ({
      period,
      from: period === 'custom' ? customFrom : bounds?.from,
      to: period === 'custom' ? customTo : bounds?.to,
      compare,
    }),
    [period, customFrom, customTo, bounds, compare],
  )

  const loadReport = useCallback(async () => {
    if (period === 'custom' && (!customFrom || !customTo)) {
      setReport(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const data = await fetchVatSummaryReport(reportOptions)
      setReport(data)
    } catch (err) {
      setReport(null)
      setError(apiErrorMessage(err, 'Unable to load VAT summary.'))
    } finally {
      setLoading(false)
    }
  }, [period, customFrom, customTo, reportOptions])

  useEffect(() => {
    loadReport()
  }, [loadReport, activeBranchId])

  const handleExport = async (format) => {
    setExporting(true)
    setError('')
    try {
      await exportVatSummaryReport(format, reportOptions)
    } catch (err) {
      setError(apiErrorMessage(err, `Unable to export ${format.toUpperCase()} report.`))
    } finally {
      setExporting(false)
    }
  }

  const hasActivity = (report?.summary?.sale_count ?? 0) > 0

  return (
    <section className="card-surface p-6 lg:p-8">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <ReceiptText className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">VAT summary</h2>
            <p className="text-sm text-muted-foreground">
              Output VAT on completed sales, refunds reversing VAT, and breakdown by tax rate.
            </p>
          </div>
        </div>
        <ReportFilterBar
          period={period}
          compare={compare}
          customFrom={customFrom}
          customTo={customTo}
          onPeriodChange={setPeriod}
          onCompareChange={setCompare}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
          onExportCsv={() => handleExport('csv')}
          onExportPdf={() => handleExport('pdf')}
          exporting={exporting}
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading report…
        </div>
      ) : error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : report ? (
        <>
          <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard label="Sales" value={String(report.summary.sale_count)} />
            <SummaryCard
              label="Output VAT"
              value={`AED ${formatReportMoney(report.summary.vat_output)}`}
            />
            <SummaryCard
              label="VAT refunded"
              value={`AED ${formatReportMoney(report.summary.vat_refunded)}`}
            />
            <SummaryCard
              label="Net VAT"
              value={`AED ${formatReportMoney(report.summary.vat_net)}`}
            />
          </div>

          {report.compare_summary ? (
            <p className="mb-4 text-sm text-muted-foreground">
              Compare period: AED {formatReportMoney(report.compare_summary.vat_net)} net VAT ·{' '}
              {report.compare_summary.sale_count} sales
            </p>
          ) : null}

          {report.by_tax_rate.length > 0 ? (
            <div className="mb-6 overflow-x-auto rounded-2xl border border-border/70">
              <p className="border-b border-border/60 bg-muted/20 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                By tax rate
              </p>
              <table className="min-w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Profile</th>
                    <th className="px-4 py-3 font-medium">Rate</th>
                    <th className="px-4 py-3 font-medium">Lines</th>
                    <th className="px-4 py-3 font-medium">Net ex VAT</th>
                    <th className="px-4 py-3 font-medium">VAT</th>
                  </tr>
                </thead>
                <tbody>
                  {report.by_tax_rate.map((row) => (
                    <tr key={row.tax_profile_id ?? 'unassigned'} className="border-t border-border/60">
                      <td className="px-4 py-3">{row.tax_profile_name}</td>
                      <td className="px-4 py-3">{row.rate}%</td>
                      <td className="px-4 py-3">{row.line_count}</td>
                      <td className="px-4 py-3">AED {formatReportMoney(row.net_ex_vat)}</td>
                      <td className="px-4 py-3">AED {formatReportMoney(row.vat_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <div className="overflow-x-auto rounded-2xl border border-border/70">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Sales</th>
                  <th className="px-4 py-3 font-medium">Refunds</th>
                  <th className="px-4 py-3 font-medium">Output VAT</th>
                  <th className="px-4 py-3 font-medium">Net VAT</th>
                </tr>
              </thead>
              <tbody>
                {!hasActivity ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                      No taxable sales in this period.
                    </td>
                  </tr>
                ) : (
                  report.by_day
                    .filter((row) => row.sale_count > 0 || row.refund_count > 0)
                    .map((row) => (
                      <tr key={row.date} className="border-t border-border/60">
                        <td className="px-4 py-3">{formatReportDate(row.date)}</td>
                        <td className="px-4 py-3">{row.sale_count}</td>
                        <td className="px-4 py-3">{row.refund_count}</td>
                        <td className="px-4 py-3">AED {formatReportMoney(row.vat_output)}</td>
                        <td className="px-4 py-3">AED {formatReportMoney(row.vat_net)}</td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  )
}

/**
 * @param {{ label: string, value: string }} props
 */
function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-border/70 bg-muted/20 px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </div>
  )
}
